/* ════════════════════════════════════════════════════════════════════════
   NUTRITION UTILS — local-only ingredient parser + nutrition estimator.
   Used by the "Calculate nutrition with ARA" button inside RecipeEditModal.

   IMPORTANT
   ─────────
   • These are *estimates* only. Always label results as estimates in UI.
   • No external API is called from this file. The structure is set up so a
     real AI/nutrition-API integration can be slotted in later via
     `estimateRecipeNutritionWithAI` without changing call sites.
   ════════════════════════════════════════════════════════════════════════ */

import { NUTRITION_REFERENCE, VOLUME_TO_ML, WEIGHT_TO_G, EACH_UNITS } from "../data/nutritionReferenceData";

/* ── 1. Parsing helpers ──────────────────────────────────────────── */

/* Parse a simple fraction or mixed number: "1", "1.5", "1/2", "1 1/2" */
function parseQuantity(raw) {
  if (raw == null) return null;
  const txt = String(raw).trim();
  if (!txt) return null;
  // Mixed number "1 1/2"
  const mixed = txt.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const num = Number(mixed[2]);
    const den = Number(mixed[3]) || 1;
    return whole + num / den;
  }
  // Pure fraction "1/2"
  const frac = txt.match(/^(\d+)\/(\d+)$/);
  if (frac) {
    return Number(frac[1]) / (Number(frac[2]) || 1);
  }
  // Decimal / whole
  const n = Number(txt);
  return Number.isFinite(n) ? n : null;
}

/* Tokenise into { qty, unit, name } from a single ingredient line.
   Examples:
     "3 eggs"             → { qty: 3, unit: "egg", name: "eggs" }
     "1/2 c sauerkraut"   → { qty: 0.5, unit: "c", name: "sauerkraut" }
     "1/2 avocado"        → { qty: 0.5, unit: null, name: "avocado" }
     "1 slice sourdough"  → { qty: 1, unit: "slice", name: "sourdough" }
     "100g chicken"       → { qty: 100, unit: "g", name: "chicken" }
     "1 cup rice"         → { qty: 1, unit: "cup", name: "rice" } */
export function parseIngredientLine(rawLine) {
  if (!rawLine || typeof rawLine !== "string") return null;
  let line = rawLine.trim().toLowerCase();
  if (!line) return null;

  // Strip leading bullets / dashes / numbers without spaces removed below.
  line = line.replace(/^[•·\-*]\s*/, "");

  // Capture leading quantity (mixed number, fraction or decimal).
  const qtyMatch = line.match(/^((?:\d+\s+\d+\/\d+)|(?:\d+\/\d+)|(?:\d+(?:\.\d+)?))\s*/);
  let qty = null;
  if (qtyMatch) {
    qty = parseQuantity(qtyMatch[1]);
    line = line.slice(qtyMatch[0].length).trim();
  }

  // "100g chicken" or "250ml milk" — capture inline unit immediately after qty
  const inlineUnitMatch = !qtyMatch ? null : line.match(/^(g|kg|ml|l)\b\s*/);
  if (inlineUnitMatch) {
    return finaliseToken(qty, inlineUnitMatch[1], line.slice(inlineUnitMatch[0].length).trim(), rawLine);
  }

  // The unit can be the first remaining word.
  const tokens = line.split(/[\s,]+/);
  if (tokens.length === 0) return null;
  const firstToken = tokens[0];
  const isWord = /^[a-z]+$/.test(firstToken || "");
  const knownVolume = isWord && VOLUME_TO_ML[firstToken] != null;
  const knownWeight = isWord && WEIGHT_TO_G[firstToken] != null;
  const knownEach   = isWord && EACH_UNITS.has(firstToken);
  if (knownVolume || knownWeight || knownEach) {
    const rest = tokens.slice(1).join(" ").trim();
    // Edge case: "3 eggs" / "2 slices" — the unit IS the name, no remainder.
    // Fall through to treat the first token as the ingredient name.
    if (rest) {
      return finaliseToken(qty, firstToken, rest, rawLine);
    }
  }

  // No explicit unit — the rest of the line is the name.
  return finaliseToken(qty, null, line, rawLine);
}

function finaliseToken(qty, unit, name, rawLine) {
  return {
    raw: rawLine,
    qty: qty == null ? 1 : qty,        // default to 1 when no qty given
    unit: unit ? unit.toLowerCase() : null,
    name: (name || "").trim(),
  };
}

/* ── 2. Ingredient name matching ─────────────────────────────────── */

/* Strip common adjectives + descriptors so "diced chicken thighs" → "chicken thighs". */
const STRIP_DESCRIPTORS = [
  "diced", "chopped", "minced", "grated", "sliced", "shredded", "crushed",
  "fresh", "frozen", "cooked", "raw", "boneless", "skinless", "drained",
  "rinsed", "softened", "melted", "large", "small", "medium", "ripe",
  "to serve", "to taste", "optional",
];

export function normaliseIngredientName(name) {
  if (!name) return "";
  let n = String(name).toLowerCase().trim();
  // Remove parenthetical notes.
  n = n.replace(/\([^)]*\)/g, "");
  // Drop trailing serving notes after a comma.
  n = n.replace(/,.*$/, "");
  // Strip common descriptors.
  STRIP_DESCRIPTORS.forEach(d => {
    n = n.replace(new RegExp(`\\b${d}\\b`, "g"), "");
  });
  // Collapse whitespace.
  return n.replace(/\s+/g, " ").trim();
}

/* Find the best matching reference entry for an ingredient name. */
function findReferenceMatch(name) {
  const clean = normaliseIngredientName(name);
  if (!clean) return null;
  let best = null;
  let bestScore = 0;
  for (const ref of NUTRITION_REFERENCE) {
    for (const alias of ref.aliases) {
      const a = alias.toLowerCase();
      if (clean === a) {
        return { ref, score: 100 };
      }
      if (clean.includes(a) || a.includes(clean)) {
        const score = Math.min(a.length, clean.length);
        if (score > bestScore) { bestScore = score; best = ref; }
      }
    }
  }
  return best ? { ref: best, score: bestScore } : null;
}

/* ── 3. Per-ingredient nutrition estimator ───────────────────────── */

const ZERO = () => ({ calories: 0, protein: 0, carbs: 0, fat: 0 });

/* Scale a reference's nutrition values to the requested amount. */
function scaleNutrition(ref, amountInRefUnit) {
  const factor = amountInRefUnit / (ref.perAmount || 1);
  return {
    calories: (ref.calories || 0) * factor,
    protein:  (ref.protein  || 0) * factor,
    carbs:    (ref.carbs    || 0) * factor,
    fat:      (ref.fat      || 0) * factor,
  };
}

/* Resolve how many of the reference unit (g / ml / each) the parsed token represents. */
function resolveAmountInRefUnit(token, ref) {
  const { qty, unit } = token;
  // "each" reference (eg. egg, avocado, slice)
  if (ref.unit === "each") {
    if (!unit) return qty;                     // "1/2 avocado"
    if (EACH_UNITS.has(unit)) return qty;      // "1 slice sourdough"
    if (WEIGHT_TO_G[unit] != null) {
      // Convert weight to count if the ref has an approx weight per item — skip if unknown.
      return null;
    }
    return null;
  }
  // Weight reference (per 100g typically)
  if (ref.unit === "g") {
    if (!unit) return null;                    // ambiguous, skip
    if (WEIGHT_TO_G[unit] != null) return qty * WEIGHT_TO_G[unit];
    if (VOLUME_TO_ML[unit] != null && ref.volumeToGramsApprox != null) {
      return qty * VOLUME_TO_ML[unit] * ref.volumeToGramsApprox;
    }
    return null;
  }
  // Volume reference (per 100ml typically)
  if (ref.unit === "ml") {
    if (!unit) return null;
    if (VOLUME_TO_ML[unit] != null) return qty * VOLUME_TO_ML[unit];
    if (WEIGHT_TO_G[unit] != null && ref.volumeToGramsApprox != null) {
      return (qty * WEIGHT_TO_G[unit]) / ref.volumeToGramsApprox;
    }
    return null;
  }
  return null;
}

export function estimateIngredientNutrition(parsedToken) {
  if (!parsedToken) return { matched: false, reason: "unparseable", nutrition: ZERO() };
  const match = findReferenceMatch(parsedToken.name);
  if (!match) return { matched: false, reason: "no-match", parsed: parsedToken, nutrition: ZERO() };
  const amount = resolveAmountInRefUnit(parsedToken, match.ref);
  if (amount == null) {
    return { matched: false, reason: "unit-mismatch", parsed: parsedToken, refName: match.ref.name, nutrition: ZERO() };
  }
  const nutrition = scaleNutrition(match.ref, amount);
  return { matched: true, parsed: parsedToken, refName: match.ref.name, nutrition };
}

/* ── 4. Recipe-level estimator + per-serve calc ──────────────────── */

export function estimateRecipeNutritionLocal(ingredients = [], servings = 1) {
  const totals = ZERO();
  const matched = [];
  const unmatched = [];

  (ingredients || []).forEach(line => {
    const parsed = parseIngredientLine(line);
    if (!parsed || !parsed.name) {
      unmatched.push({ raw: line, reason: "empty" });
      return;
    }
    const result = estimateIngredientNutrition(parsed);
    if (!result.matched) {
      unmatched.push({ raw: line, reason: result.reason, parsed });
      return;
    }
    totals.calories += result.nutrition.calories;
    totals.protein  += result.nutrition.protein;
    totals.carbs    += result.nutrition.carbs;
    totals.fat      += result.nutrition.fat;
    matched.push({ raw: line, refName: result.refName });
  });

  const perServe = calculatePerServeNutrition(totals, servings);
  const confidence = getNutritionConfidence(matched.length, unmatched.length);
  return {
    totals: roundNutrition(totals, { caloriesDp: 0, macroDp: 1 }),
    perServe,
    matched,
    unmatched,
    confidence,
    source: "local-reference",
    estimatedAt: new Date().toISOString(),
    notes: buildNutritionEstimateNotes(matched, unmatched, confidence),
  };
}

/* Placeholder for a future AI-backed estimator — same return shape so callers
   don't need to branch. Falls back to the local estimator until a real
   backend exists. */
export async function estimateRecipeNutritionWithAI(ingredients = [], servings = 1) {
  // TODO: when an AI/nutrition backend is wired up, call it here. The local
  // estimator is the safe fallback so the UI works even when offline.
  return estimateRecipeNutritionLocal(ingredients, servings);
}

export function calculatePerServeNutrition(totals, servings) {
  const n = Number(servings) || 0;
  if (n <= 0) return { calories: null, protein: null, carbs: null, fat: null };
  return {
    calories: +(totals.calories / n).toFixed(0),
    protein:  +(totals.protein  / n).toFixed(1),
    carbs:    +(totals.carbs    / n).toFixed(1),
    fat:      +(totals.fat      / n).toFixed(1),
  };
}

export function roundNutrition(n, { caloriesDp = 0, macroDp = 1 } = {}) {
  const k = (v, dp) => +Number(v || 0).toFixed(dp);
  return {
    calories: k(n.calories, caloriesDp),
    protein:  k(n.protein,  macroDp),
    carbs:    k(n.carbs,    macroDp),
    fat:      k(n.fat,      macroDp),
  };
}

export function getNutritionConfidence(matchedCount, unmatchedCount) {
  const total = matchedCount + unmatchedCount;
  if (total === 0) return "Low";
  const ratio = matchedCount / total;
  if (ratio >= 0.85 && matchedCount >= 3) return "Good";
  if (ratio >= 0.6) return "Medium";
  return "Low";
}

export function buildNutritionEstimateNotes(matched, unmatched, confidence) {
  const lines = [];
  lines.push(`ARA estimated this recipe from ${matched.length + unmatched.length} ingredient line${matched.length + unmatched.length === 1 ? "" : "s"}.`);
  if (unmatched.length === 0) {
    lines.push("All ingredients matched the reference table.");
  } else {
    lines.push(`${unmatched.length} ingredient${unmatched.length === 1 ? "" : "s"} couldn't be matched and weren't included in the totals.`);
  }
  lines.push(`Confidence: ${confidence}.`);
  lines.push("Estimated nutrition only. Brands, serving sizes and ingredient weights can change the result.");
  return lines.join(" ");
}
